import React, { useEffect, useMemo, useState } from 'react';
import {
  addServiceTicketTimelineEntry,
  createClientServiceTicket,
  getServiceTicketDetails,
  listClientServiceTickets,
  updateServiceTicketStatus,
} from '../../api/backend';

const defaultForm = {
  title: '',
  description: '',
  priority: 'media',
};

const PRIORITY_COLORS = {
  baja: 'bg-gray-100 text-gray-700',
  media: 'bg-blue-100 text-blue-700',
  alta: 'bg-amber-100 text-amber-700',
  critica: 'bg-red-100 text-red-700',
};

const STATUS_LABELS = {
  abierto: 'Abierto',
  en_proceso: 'En proceso',
  en_espera_cliente: 'En espera',
  resuelto: 'Resuelto',
  cerrado: 'Cerrado',
};

const formatDateTime = (value) => {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime())
    ? date.toLocaleString('es-PR', { hour: '2-digit', minute: '2-digit', month: 'short', day: 'numeric' })
    : '—';
};

const ClientService = () => {
  const [form, setForm] = useState(defaultForm);
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [note, setNote] = useState('');
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [loadingTimeline, setLoadingTimeline] = useState(false);

  const priorityOptions = useMemo(
    () => [
      { value: 'baja', label: 'Baja' },
      { value: 'media', label: 'Media' },
      { value: 'alta', label: 'Alta' },
      { value: 'critica', label: 'Crítica' },
    ],
    []
  );

  useEffect(() => {
    const fetchTickets = async () => {
      setLoadingTickets(true);
      try {
        const response = await listClientServiceTickets();
        setTickets(response.tickets || []);
      } catch (err) {
        console.error('❌ Error cargando tickets del cliente:', err);
      } finally {
        setLoadingTickets(false);
      }
    };
    fetchTickets();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;

    try {
      const ticket = await createClientServiceTicket({
        title: form.title.trim(),
        description: form.description.trim(),
        priority: form.priority,
      });
      setForm(defaultForm);
      setTickets((prev) => [ticket, ...prev]);
    } catch (err) {
      console.error('❌ Error creando ticket:', err);
    }
  };

  const openTicket = async (ticket) => {
    setSelectedTicket(ticket);
    setLoadingTimeline(true);
    try {
      const response = await getServiceTicketDetails({ ticketId: ticket.id });
      setSelectedTicket(response.ticket);
      setTimeline(response.timeline || []);
    } catch (err) {
      console.error('❌ Error cargando timeline del ticket:', err);
    } finally {
      setLoadingTimeline(false);
    }
  };

  const addNote = async () => {
    if (!selectedTicket || !note.trim()) return;
    try {
      const response = await addServiceTicketTimelineEntry({
        ticketId: selectedTicket.id,
        summary: note.trim(),
      });
      setNote('');
      setTimeline(response.timeline || []);
      const refreshed = await getServiceTicketDetails({ ticketId: selectedTicket.id });
      setSelectedTicket(refreshed.ticket);
      setTickets((prev) => prev.map((t) => (t.id === refreshed.ticket.id ? refreshed.ticket : t)));
    } catch (err) {
      console.error('❌ Error añadiendo nota:', err);
    }
  };

  const markResolved = async () => {
    if (!selectedTicket) return;
    try {
      const updatedTicket = await updateServiceTicketStatus({
        ticketId: selectedTicket.id,
        status: 'resuelto',
      });
      setTickets((prev) =>
        prev.map((t) => (t.id === updatedTicket.id ? updatedTicket : t))
      );
      setSelectedTicket(updatedTicket);
    } catch (err) {
      console.error('❌ Error actualizando ticket:', err);
    }
  };

  return (
    <div className="space-y-8">
      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Solicitar servicio</h2>
        <form className="grid gap-4" onSubmit={handleCreate}>
          <input
            type="text"
            name="title"
            value={form.title}
            onChange={handleChange}
            placeholder="Título del servicio"
            className="border rounded px-3 py-2"
          />
          <textarea
            name="description"
            value={form.description}
            onChange={handleChange}
            placeholder="Describe lo que necesitas"
            className="border rounded px-3 py-2 min-h-[120px]"
          />
          <div>
            <label className="text-sm font-medium text-gray-600">Prioridad</label>
            <select
              name="priority"
              value={form.priority}
              onChange={handleChange}
              className="border rounded px-3 py-2 w-full mt-1"
            >
              {priorityOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <button
            type="submit"
            className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700"
          >
            Crear ticket
          </button>
        </form>
      </section>

      <section className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-800">Mis tickets</h2>
          {loadingTickets && <p className="text-sm text-gray-500">Cargando…</p>}
        </div>
        {tickets.length === 0 ? (
          <p className="text-sm text-gray-500">Aún no has creado tickets de servicio.</p>
        ) : (
          <div className="space-y-3">
            {tickets.map((ticket) => (
              <article
                key={ticket.id}
                className="border rounded-lg px-4 py-3 flex flex-col gap-2 cursor-pointer hover:bg-gray-50"
                onClick={() => openTicket(ticket)}
              >
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-800">{ticket.title}</h3>
                  <span
                    className={`text-xs px-2 py-1 rounded-full ${
                      PRIORITY_COLORS[ticket.priority] || 'bg-gray-100'
                    }`}
                  >
                    {ticket.priority?.toUpperCase()}
                  </span>
                </div>
                <p className="text-sm text-gray-600 line-clamp-2">{ticket.description}</p>
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <span className="font-medium">
                    {STATUS_LABELS[ticket.status] || ticket.status}
                  </span>
                  {ticket.updatedAt && (
                    <span>
                      Actualizado {new Date(ticket.updatedAt).toLocaleDateString('es-PR', {
                        month: 'short',
                        day: 'numeric',
                      })}
                    </span>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {selectedTicket && (
        <section className="bg-white rounded-lg shadow p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-800">{selectedTicket.title}</h3>
              <p className="text-sm text-gray-500">
                Estado: {STATUS_LABELS[selectedTicket.status] || selectedTicket.status}
              </p>
            </div>
            {selectedTicket.status !== 'resuelto' && (
              <button
                type="button"
                onClick={markResolved}
                className="px-4 py-2 rounded-lg bg-emerald-100 text-emerald-700 text-sm font-medium"
              >
                Marcar como resuelto
              </button>
            )}
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Línea de tiempo</h4>
            {loadingTimeline ? (
              <p className="text-sm text-gray-500">Cargando actualizaciones…</p>
            ) : timeline.length === 0 ? (
              <p className="text-sm text-gray-500">Sin notas todavía.</p>
            ) : (
              <ul className="space-y-3 max-h-64 overflow-y-auto pr-2">
                {timeline.map((entry) => (
                  <li key={entry.id} className="border rounded-lg px-3 py-2">
                    <p className="text-sm text-gray-800">{entry.summary}</p>
                    <div className="flex items-center justify-between text-xs text-gray-500 mt-1">
                      <span>{entry.createdBy || entry.channel}</span>
                      <span>{formatDateTime(entry.createdAt)}</span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="space-y-2">
            <textarea
              className="border rounded px-3 py-2 w-full"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Agrega una nota o actualiza al equipo"
            />
            <button
              type="button"
              onClick={addNote}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium"
            >
              Enviar nota
            </button>
          </div>
        </section>
      )}
    </div>
  );
};

export default ClientService;
